import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoInventoryRpcClientContext } from "@redotech/redo-merchant-app-common/inventory-rpc-client-provider";
import type { TransformProductState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  isTransformProductMapMode,
  TRANSFORM_PRODUCT_MAPPED_PATH_LABEL,
  TRANSFORM_PRODUCT_MODE_LABELS,
  TRANSFORM_PRODUCT_UNMAPPED_PATH_LABEL,
  TransformProductMode,
  visibleTransformProductModes,
} from "@redotech/redo-return-core-common/return-flow/transform-product-mode";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { useNavigate } from "react-router-dom";

export const PRODUCT_TRANSFORM_SETTINGS_PATH =
  "/settings/warehouse/product-transform";

export const TransformProductDetails = memo(function TransformProductDetails({
  stepSelect,
  state,
  setState,
}: StepTypeDetailsProps<TransformProductState>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rpcClient = useRequiredContext(RedoInventoryRpcClientContext);
  const connectionLoad = useLoad(
    () => rpcClient.getProductGenerationRule({}),
    [rpcClient],
  );
  const hasRecommerceSourceConnection =
    connectionLoad.value?.hasConnectedAccounts === true;
  const mode = state.mode ?? TransformProductMode.Map;
  const mapMode = isTransformProductMapMode(mode);
  const modeOptions = useMemo(
    () =>
      visibleTransformProductModes({
        currentMode: state.mode,
        hasRecommerceSourceConnection,
      }).map((value) => ({
        type: "text" as const,
        id: value,
        text: TRANSFORM_PRODUCT_MODE_LABELS[value],
        value,
      })),
    [hasRecommerceSourceConnection, state.mode],
  );

  return (
    <Flex dir="column" gap="3xl">
      <Flex dir="column" gap="md">
        <Flex mb="sm">
          <RedoSingleSelectDropdownInput<TransformProductMode>
            dataTestId="transform-product-mode"
            fullWidth
            label={t("Transformation method")}
            options={modeOptions}
            optionSelected={({ item }) =>
              setState((current) => {
                if (current.mode === item.value) {
                  return current;
                }
                if (isTransformProductMapMode(item.value)) {
                  return {
                    ...current,
                    mode: item.value,
                    next: undefined,
                    nextMapped: current.nextMapped ?? current.next,
                  };
                }
                return {
                  ...current,
                  mode: item.value,
                  next: current.next ?? current.nextMapped,
                  nextMapped: undefined,
                  nextUnmapped: undefined,
                };
              })
            }
            selectedItem={modeOptions.find((option) => option.value === mode)}
            size="xs"
          />
        </Flex>
        <Text fontSize="xs" fontWeight="regular" textColor="secondary">
          {t(
            "Products can be transformed to other SKUs for resale. Configure those mappings in product transform settings.",
          )}
        </Text>
        <RedoButton
          hierarchy="secondary"
          onClick={() => navigate(PRODUCT_TRANSFORM_SETTINGS_PATH)}
          size="sm"
          text={t("Product transform settings")}
        />
      </Flex>
      <Divider />
      <Flex dir="column" gap="md">
        {mapMode ? (
          <>
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                {TRANSFORM_PRODUCT_MAPPED_PATH_LABEL}
              </Text>
              {stepSelect({
                value: state.nextMapped,
                valueChange: (nextMapped) =>
                  setState((current) => ({ ...current, nextMapped })),
                handleIndex: 0,
              })}
            </Flex>
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                {TRANSFORM_PRODUCT_UNMAPPED_PATH_LABEL}
              </Text>
              {stepSelect({
                value: state.nextUnmapped,
                valueChange: (nextUnmapped) =>
                  setState((current) => ({ ...current, nextUnmapped })),
                handleIndex: 1,
              })}
            </Flex>
          </>
        ) : (
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              {t("Next step")}
            </Text>
            {stepSelect({
              value: state.next,
              valueChange: (next) =>
                setState((current) => ({ ...current, next })),
              handleIndex: 0,
            })}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});
